import { Dispatch, SetStateAction, useState, useEffect, useCallback, useMemo } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import {
  Modal,
  Button,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import * as Yup from "yup";
import { isValidInput } from "../utils/validations";
import useTranslation from "../hooks/traslation";
import Loader from "../constants/Loader";
import {
  useCategoryByIdQuery,
  useCategoryInfiniteListQuery,
} from "../hooks/useCategoryQueries";
import { SUPPORTED_LANGUAGES } from "../constants/languages";
import type { PromotionalBannerTexts } from "../services/promotionalBanners";

interface CategoryOption {
  _id: string;
  name?: string;
}

interface AddPromotionalBannerModalProps {
  open: boolean;
  onClose: () => void;
  category: string;
  texts: PromotionalBannerTexts;
  status: boolean;
  isPrimary: boolean;
  selectedId: string;
  setCategory: Dispatch<SetStateAction<string>>;
  setTexts: Dispatch<SetStateAction<PromotionalBannerTexts>>;
  setStatus: Dispatch<SetStateAction<boolean>>;
  setIsPrimary: Dispatch<SetStateAction<boolean>>;
  setSelectedId: Dispatch<SetStateAction<string>>;
  handleSubmit: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isFormLoading?: boolean;
}

export default function AddPromotionalBanner({
  open,
  setOpen,
  category,
  texts,
  status,
  isPrimary,
  setCategory,
  setTexts,
  setStatus,
  setIsPrimary,
  handleSubmit,
  selectedId,
  setSelectedId,
  onClose,
  isFormLoading = false,
}: AddPromotionalBannerModalProps) {
  const [errors, setErrors] = useState<Record<string, string>>({ category: "" });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const translate = useTranslation();

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setDebouncedSearch("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search, open]);

  const isEditWithCategory = open && !!category;
  const shouldFetchList = open && (!category || debouncedSearch !== "");

  const { data: categoryByIdData } = useCategoryByIdQuery(
    category,
    isEditWithCategory
  );

  const {
    data: infiniteData,
    isLoading: isLoadingCategories,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useCategoryInfiniteListQuery(debouncedSearch, shouldFetchList);

  const categories = useMemo(() => {
    const listFromInfinite =
      infiniteData?.pages.flatMap((p) => p.list as CategoryOption[]) ?? [];
    if (categoryByIdData && category && debouncedSearch === "") {
      return [categoryByIdData as CategoryOption];
    }
    if (
      categoryByIdData &&
      category === (categoryByIdData as CategoryOption)._id &&
      !listFromInfinite.some((c) => c._id === (categoryByIdData as CategoryOption)._id)
    ) {
      return [categoryByIdData as CategoryOption, ...listFromInfinite];
    }
    return listFromInfinite;
  }, [infiniteData, categoryByIdData, category, debouncedSearch]);

  const hasMore = !!hasNextPage;

  const handleInputChange = useCallback((_: unknown, value: string) => {
    setSearch(value);
  }, []);

  const handleListboxScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement>) => {
      const target = e.currentTarget;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 50;
      if (nearBottom && !isFetchingNextPage && hasMore) fetchNextPage();
    },
    [fetchNextPage, isFetchingNextPage, hasMore]
  );

  const selectedCategory =
    categories.find((c) => c._id === category) ?? null;

  const textsShape = SUPPORTED_LANGUAGES.reduce<
    Record<string, Yup.StringSchema<string | undefined>>
  >((acc, { code }) => {
    acc[code] = Yup.string()
      .required(
        translate?.promotionalBanner?.errorText || "Text is required"
      )
      .min(
        2,
        translate?.promotionalBanner?.errorMin || "Minimum 2 characters"
      )
      .max(
        500,
        translate?.promotionalBanner?.errorTextMax ||
          "Maximum 500 characters"
      );
    return acc;
  }, {});

  const bannerSchema = Yup.object().shape({
    category: Yup.string().required(
      translate?.promotionalBanner?.errorCategory || "Category is required"
    ),
    texts: Yup.object().shape(textsShape),
  });

  const validateForm = async () => {
    try {
      await bannerSchema.validate({ category, texts }, { abortEarly: false });
      setErrors({ category: "" });
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: Record<string, string> = { category: "" };
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleFormSubmit = async () => {
    if (await validateForm()) {
      handleSubmit();
    }
  };

  const handleClose = () => {
    if (isFormLoading) return;
    setSelectedId("");
    setOpen(false);
    setErrors({ category: "" });
  };

  return (
    <Modal
      className="modal addCategory_modal"
      open={open}
      onClose={isFormLoading ? undefined : handleClose}
    >
      <div className="modal-dialog w_900">
        <div className="modal-body">
          <Loader isLoad={isFormLoading} />
          <div className="modal_title">
            <h2>
              {selectedId
                ? translate?.promotionalBanner?.updateBanner || "Update Banner"
                : translate?.promotionalBanner?.addBanner || "Add Promotional Banner"}
            </h2>
            <div className="btn-close" onClick={handleClose}>
              <CloseIcon />
            </div>
          </div>
          <form className="form" action="">
            <div className="gap_p">
              <div className="control_group w_50 autocomplete-no-inner-border">
                <Autocomplete
                  value={selectedCategory}
                  onChange={(_, newValue) => {
                    setCategory(newValue?._id ?? "");
                    setErrors((prev) => ({ ...prev, category: "" }));
                  }}
                  onInputChange={handleInputChange}
                  options={categories}
                  getOptionLabel={(opt) => opt?.name ?? opt?._id ?? ""}
                  isOptionEqualToValue={(opt, val) => opt._id === val._id}
                  loading={isLoadingCategories || isFetchingNextPage}
                  disabled={isFormLoading}
                  filterOptions={(x) => x}
                  ListboxProps={{
                    onScroll: handleListboxScroll,
                    style: { maxHeight: 200 },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate?.promotionalBanner?.category || "Category"}
                      error={!!errors.category}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingCategories ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {errors.category && (
                  <span
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.category}
                  </span>
                )}
              </div>

              {SUPPORTED_LANGUAGES.map(({ code, label }) => (
                <div key={code} className="control_group w_100">
                  <label>{translate?.promotionalBanner?.text || "Text"} ({label})</label>
                  <TextField
                    placeholder={`Enter banner text (${label})`}
                    fullWidth
                    multiline
                    rows={3}
                    value={texts[code] ?? ""}
                    onChange={(e) => {
                      if (isValidInput(e.target.value)) {
                        setTexts((prev) => ({ ...prev, [code]: e.target.value }));
                        setErrors((prev) => ({ ...prev, [`texts.${code}`]: "" }));
                      }
                    }}
                    error={!!errors[`texts.${code}`]}
                    hiddenLabel
                    disabled={isFormLoading}
                  />
                  {errors[`texts.${code}`] && (
                    <span
                      className="error-message"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      {errors[`texts.${code}`]}
                    </span>
                  )}
                </div>
              ))}

              <div className="control_group w_50">
                <FormControlLabel
                  control={
                    <Switch
                      checked={status}
                      onChange={(e) => setStatus(e.target.checked)}
                      disabled={isFormLoading}
                    />
                  }
                  label={
                    translate?.promotionalBanner?.status || translate?.globals?.status || "Status"
                  }
                />
              </div>

              <div className="control_group w_50">
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPrimary}
                      onChange={(e) => setIsPrimary(e.target.checked)}
                      disabled={isFormLoading}
                    />
                  }
                  label={
                    translate?.promotionalBanner?.isPrimary || "Primary"
                  }
                />
              </div>

              <div className="footer_btn_flex">
                <Button
                  className="btn btn_grey"
                  onClick={handleClose}
                  disabled={isFormLoading}
                >
                  {translate?.main?.cancel || "Cancel"}
                </Button>
                <Button
                  className="btn btn_primary"
                  onClick={handleFormSubmit}
                  disabled={isFormLoading}
                >
                  {selectedId
                    ? translate?.main?.update || "Update"
                    : translate?.globals?.submit || "Submit"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
